import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Check, CheckCircle2, Loader2, Lock, MessageCircle, CalendarRange, Users, BedDouble,
  UtensilsCrossed, Navigation, Car, ShieldCheck, Zap, Flame,
} from "lucide-react";
import { AeroNav } from "@/components/aero/AeroNav";
import { AeroFooter } from "@/components/aero/AeroFooter";
import { WhatsAppFloat } from "@/components/cine/WhatsAppFloat";
import { SOCIALS } from "@/constants/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];
const BATCH_TARGET = new Date("2026-10-02T09:00:00+05:30").getTime();

const PLANS = {
  advance: { label: "Reserve with Advance", amount: 2000, note: "Remaining amount can be paid before the workshop.", tag: "Most Popular", cta: "Reserve My Seat" },
  full: { label: "Pay Full Amount", amount: 12000, note: "Instant confirmation.", tag: "Instant Confirmation", cta: "Pay Full & Confirm" },
};

const PAY_METHODS = ["UPI", "Google Pay", "PhonePe", "Paytm", "Credit Card", "Debit Card", "Net Banking"];

const TRUST_CARDS = [
  { icon: Navigation, title: "DJI Avata 2", sub: "Hands-on training" },
  { icon: BedDouble, title: "Hotel", sub: "2 nights stay" },
  { icon: UtensilsCrossed, title: "Meals", sub: "All included" },
  { icon: Car, title: "Local Transport", sub: "Covered" },
];

const inputCls =
  "w-full bg-[#F8F6F2] border border-[#E5E7EB] focus:border-[#4F6D8A] focus:shadow-[0_0_0_4px_rgba(79,109,138,0.12)] outline-none rounded-2xl px-5 py-4 text-sm text-[#111827] placeholder:text-[#9CA3AF] transition-all duration-300";

const loadRazorpay = () =>
  new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });

const useCountdown = () => {
  const [left, setLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, BATCH_TARGET - Date.now());
      setLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);
  return left;
};

const StepHead = ({ n, title, sub }) => (
  <div className="mb-10">
    <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A]">Step {n}</p>
    <h2 className="mt-3 font-serif text-[32px] md:text-[44px] leading-[1.06] text-[#111827]">{title}</h2>
    {sub && <p className="mt-3 text-[#6B7280] leading-relaxed max-w-xl">{sub}</p>}
    <span className="mt-5 block w-14 h-[2px] bg-[#4F6D8A]" />
  </div>
);

export default function BookWorkshopPage() {
  const navigate = useNavigate();
  const [seats, setSeats] = useState(null);
  const [batch, setBatch] = useState("oct_2026");
  const [plan, setPlan] = useState("advance");
  const [paying, setPaying] = useState(false);
  const [waHref, setWaHref] = useState(SOCIALS.instagram);
  const glowRef = useRef(null);
  const payRef = useRef(null);
  const countdown = useCountdown();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", city: "", age: "",
    emergency_name: "", emergency_phone: "", experience: "beginner", owns_drone: "no", requirements: "",
  });

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/workshop/seats`).then((r) => setSeats(r.data)).catch(() => {});
    axios
      .get(`${API}/config`)
      .then((r) => {
        if (r.data.whatsapp_number) {
          setWaHref(`https://wa.me/${r.data.whatsapp_number}?text=${encodeURIComponent("Hi Akhilesh, I want to reserve my seat for the DJI Avata 2 FPV Masterclass.")}`);
        }
      })
      .catch(() => {});
  }, []);

  const onMouseMove = (e) => {
    if (glowRef.current) {
      glowRef.current.style.background = `radial-gradient(650px at ${e.clientX}px ${e.clientY + window.scrollY}px, rgba(79,109,138,0.09), transparent 70%)`;
    }
  };

  const pay = async (chosenPlan) => {
    setPlan(chosenPlan);
    if (form.name.trim().length < 2 || form.phone.trim().length < 10 || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Pehle apna naam, phone aur email bharo (Step 2)");
      document.querySelector("[data-testid='bw-form']")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("rzp load failed");
      const { data } = await axios.post(`${API}/workshop-bookings/create-order`, { ...form, batch, plan: chosenPlan });
      const rzp = new window.Razorpay({
        key: data.key_id,
        order_id: data.order_id,
        amount: data.amount,
        currency: "INR",
        name: "FILMMAKERAKKI",
        description: "DJI Avata 2 FPV Masterclass",
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#0F2A43" },
        handler: async (resp) => {
          try {
            const v = await axios.post(`${API}/workshop-bookings/verify`, {
              booking_id: data.booking_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            navigate(`/booking-success?bid=${v.data.booking_id}`);
          } catch {
            toast.error("Payment verify nahi hua — support se contact karo.");
          }
        },
        modal: { ondismiss: () => toast.error("Payment cancel hua — seat abhi reserve nahi hui.") },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment start nahi ho paya — dobara try karo.");
    } finally {
      setPaying(false);
    }
  };

  const batchInfo = seats?.[batch];

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      onMouseMove={onMouseMove}
      className="relative bg-[#F8F6F2] text-[#111827] overflow-x-clip"
      data-testid="book-workshop-page"
    >
      <div ref={glowRef} aria-hidden className="pointer-events-none absolute inset-0 z-0 hidden lg:block" />
      <AeroNav />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="bw-hero">
        <div className="relative min-h-[420px] md:min-h-[480px] flex items-center">
          <img src="/assets/hero-main.jpg" alt="Akhilesh Kumar Suman in the Himalayas with drone" className="absolute inset-0 w-full h-full object-cover object-[center_35%]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F8F6F2] via-[#F8F6F2]/75 to-[#F8F6F2]/15" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#F8F6F2] to-transparent" />
          <div className="relative z-10 max-w-[1320px] mx-auto px-6 md:px-10 w-full pt-32 pb-20">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease }} className="max-w-2xl">
              <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A] flex items-center gap-4">
                <span className="w-10 h-px bg-[#4F6D8A]/60 inline-block" /> DJI Avata 2 FPV Masterclass
              </p>
              <h1 className="mt-4 font-serif text-[42px] md:text-[64px] leading-[1.03] text-[#111827]">Reserve Your Seat</h1>
              <p className="mt-4 text-[#374151] leading-relaxed max-w-lg">
                You're one step away from joining an exclusive DJI Avata 2 FPV Masterclass designed for serious creators.
              </p>
              {/* urgency countdown — ticking time bomb */}
              <motion.div
                initial={{ opacity: 0, y: 26, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.35, ease }}
                className="mt-9 relative max-w-xl"
                data-testid="bw-countdown"
              >
                {/* alarm glow pulsing behind the card */}
                <motion.span
                  aria-hidden
                  animate={{ opacity: [0.3, 0.7, 0.3], scale: [1, 1.03, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -inset-1.5 rounded-[28px] bg-[#B3261E]/30 blur-2xl"
                />
                <div className="relative bg-[#0F2A43] rounded-[24px] px-6 py-6 md:px-8 md:py-7 shadow-[0_40px_80px_-22px_rgba(15,42,67,0.65)] overflow-hidden">
                  {/* burning fuse line */}
                  <motion.span
                    aria-hidden
                    className="absolute top-0 left-0 h-[3px] rounded-r-full bg-gradient-to-r from-transparent via-[#FF4D24] to-[#FFB199]"
                    animate={{ width: ["4%", "100%"] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
                  />
                  <div className="flex items-center justify-between gap-4">
                    <p className="font-mono text-[10px] font-bold tracking-[0.28em] uppercase text-white flex items-center gap-2.5">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF4D24] opacity-80" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF4D24]" />
                      </span>
                      Next batch starts in
                    </p>
                    <p className="font-mono text-[9px] font-bold tracking-[0.25em] uppercase text-[#FF8A6B] flex items-center gap-1.5">
                      <Flame size={11} /> Filling Fast
                    </p>
                  </div>
                  {/* flip-clock digits */}
                  <div className="mt-5 flex items-stretch gap-2 md:gap-3">
                    {[
                      { v: countdown.d, l: "Days" },
                      { v: countdown.h, l: "Hrs" },
                      { v: countdown.m, l: "Min" },
                      { v: countdown.s, l: "Sec" },
                    ].map((u, i) => (
                      <Fragment key={u.l}>
                        <div className="flex-1 bg-white/[0.07] border border-white/10 rounded-2xl px-2 py-3.5 text-center">
                          <p className="font-mono font-bold text-[30px] md:text-[40px] leading-none text-white tabular-nums" data-testid={`bw-cd-${u.l.toLowerCase()}`}>
                            {String(u.v).padStart(2, "0")}
                          </p>
                          <p className="mt-2 font-mono text-[8px] tracking-[0.3em] uppercase text-[#8FAECC]">{u.l}</p>
                        </div>
                        {i < 3 && (
                          <motion.span
                            aria-hidden
                            animate={{ opacity: [1, 0.1, 1] }}
                            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
                            className="self-center font-mono font-bold text-2xl md:text-3xl text-[#FF4D24]"
                          >
                            :
                          </motion.span>
                        )}
                      </Fragment>
                    ))}
                  </div>
                  <p className="mt-4 flex items-center gap-2 font-mono text-[9px] font-bold tracking-[0.18em] uppercase text-[#8FAECC]" data-testid="bw-countdown-seats">
                    <Zap size={11} className="text-[#FF4D24] shrink-0" />
                    Sirf {batchInfo?.remaining ?? 10} seats bachi hain — batch full hote hi registration band
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
        {/* Trust strip */}
        <div className="max-w-[1320px] mx-auto px-6 md:px-10 relative z-20 -mt-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: CalendarRange, t: "3 Days", s: "Immersive Experience" },
              { icon: Users, t: "Only 10 Seats", s: "Small Batch" },
              { icon: BedDouble, t: "Stay Included", s: "Hotel" },
              { icon: UtensilsCrossed, t: "Meals Included", s: "+ Transport" },
            ].map((x, i) => {
              const Icon = x.icon;
              return (
                <motion.div
                  key={x.t}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.07, ease }}
                  data-testid={`bw-trust-${i}`}
                  className="bg-white rounded-[18px] border border-[#E5E7EB] shadow-[0_15px_40px_-18px_rgba(15,42,67,0.25)] px-5 py-5 flex items-center gap-3.5"
                >
                  <span className="w-10 h-10 rounded-full bg-[#4F6D8A]/10 flex items-center justify-center shrink-0">
                    <Icon size={17} strokeWidth={1.4} className="text-[#0F2A43]" />
                  </span>
                  <div>
                    <p className="font-display font-bold text-sm text-[#111827]">{x.t}</p>
                    <p className="font-mono text-[8px] tracking-[0.18em] uppercase text-[#6B7280] mt-0.5">{x.s}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Step 1 — Batch */}
      <section className="py-[80px] md:py-[100px]" data-testid="bw-step-1">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10">
          <StepHead n="01" title="Choose Your Batch" />
          <div className="grid md:grid-cols-2 gap-7 max-w-4xl">
            {["oct_2026", "nov_2026"].map((key, i) => {
              const info = seats?.[key];
              const remaining = info?.remaining ?? 10;
              const total = info?.total ?? 10;
              const active = batch === key;
              return (
                <motion.button
                  key={key}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.55, delay: i * 0.1, ease }}
                  onClick={() => setBatch(key)}
                  data-testid={`bw-batch-${key}`}
                  className={`group relative bg-white rounded-[22px] border p-8 text-left transition-all duration-300 hover:-translate-y-1.5 ${
                    active
                      ? "border-[#0F2A43] shadow-[0_30px_65px_-20px_rgba(15,42,67,0.45)] ring-1 ring-[#0F2A43]"
                      : "border-[#E5E7EB] shadow-[0_12px_35px_-15px_rgba(15,42,67,0.15)] hover:shadow-[0_30px_60px_-20px_rgba(15,42,67,0.3)]"
                  }`}
                >
                  {active && (
                    <>
                      <span className="absolute top-5 right-5 w-7 h-7 rounded-full bg-[#0F2A43] flex items-center justify-center">
                        <Check size={13} strokeWidth={3} className="text-white" />
                      </span>
                      <span className="absolute -top-3 left-8 bg-[#0F2A43] text-white font-mono text-[8px] font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full">
                        Selected
                      </span>
                    </>
                  )}
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4F6D8A]">{info?.note || "Weekend Batch"}</p>
                  <p className="mt-3 font-display font-bold text-2xl text-[#111827]">{info?.label || (key === "oct_2026" ? "October 2026" : "November 2026")}</p>
                  {/* live seat counter */}
                  <div className="mt-6">
                    <div className="flex items-center justify-between font-mono text-[9px] tracking-[0.2em] uppercase text-[#6B7280]">
                      <span data-testid={`bw-seats-${key}`}>{remaining} / {total} Seats Remaining</span>
                      <span>{total - remaining} booked</span>
                    </div>
                    <div className="mt-2.5 h-2 rounded-full bg-[#E5E7EB] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(remaining / total) * 100}%` }}
                        transition={{ duration: 1, ease }}
                        className="h-full rounded-full bg-gradient-to-r from-[#4F6D8A] to-[#0F2A43]"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <div>
                      <p className="font-mono text-[8px] tracking-[0.2em] uppercase text-[#6B7280]">Duration · Price</p>
                      <p className="mt-1 font-display font-bold text-lg text-[#0F2A43]">3 Days · ₹12,000</p>
                    </div>
                    <span className={`inline-flex items-center gap-2 font-mono text-[9px] font-bold tracking-[0.2em] uppercase px-5 py-3 rounded-full transition-colors ${active ? "bg-[#0F2A43] text-white" : "border border-[#0F2A43]/25 text-[#0F2A43] group-hover:bg-[#EEF2F6]"}`}>
                      Choose This Batch
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Steps 2 + 3 (form + sticky summary) */}
      <section className="pb-[80px] md:pb-[100px]">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          {/* Step 2 — form */}
          <div className="lg:col-span-7">
            <StepHead n="02" title="Participant Information" />
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, ease }}
              className="bg-white border border-[#E5E7EB] rounded-[22px] p-7 md:p-10 shadow-[0_25px_70px_-25px_rgba(15,42,67,0.25)]"
              data-testid="bw-form"
            >
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4F6D8A]">Personal Details</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <input data-testid="bw-name" value={form.name} onChange={set("name")} placeholder="Full Name" className={inputCls} />
                <input data-testid="bw-phone" value={form.phone} onChange={set("phone")} placeholder="Mobile Number" className={inputCls} />
                <input data-testid="bw-email" type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
                <input data-testid="bw-city" value={form.city} onChange={set("city")} placeholder="City" className={inputCls} />
                <input data-testid="bw-age" value={form.age} onChange={set("age")} placeholder="Age" className={inputCls} />
              </div>
              <p className="mt-8 font-mono text-[10px] tracking-[0.3em] uppercase text-[#4F6D8A]">Emergency Contact</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <input data-testid="bw-emergency-name" value={form.emergency_name} onChange={set("emergency_name")} placeholder="Emergency Contact Name" className={inputCls} />
                <input data-testid="bw-emergency-phone" value={form.emergency_phone} onChange={set("emergency_phone")} placeholder="Emergency Contact Number" className={inputCls} />
              </div>
              <p className="mt-8 font-mono text-[10px] tracking-[0.3em] uppercase text-[#4F6D8A]">Experience Level</p>
              <div className="mt-4 flex flex-wrap gap-3" data-testid="bw-experience">
                {["beginner", "intermediate", "experienced"].map((lv) => (
                  <button
                    key={lv}
                    onClick={() => setForm((p) => ({ ...p, experience: lv }))}
                    data-testid={`bw-exp-${lv}`}
                    className={`font-mono text-[10px] tracking-[0.18em] uppercase px-6 py-3 rounded-full border transition-all duration-300 ${
                      form.experience === lv ? "bg-[#0F2A43] text-white border-[#0F2A43]" : "border-[#E5E7EB] text-[#6B7280] hover:border-[#0F2A43]/40"
                    }`}
                  >
                    {lv}
                  </button>
                ))}
              </div>
              <p className="mt-8 font-mono text-[10px] tracking-[0.3em] uppercase text-[#4F6D8A]">Do You Own a Drone?</p>
              <div className="mt-4 flex gap-3" data-testid="bw-owns-drone">
                {["yes", "no"].map((v) => (
                  <button
                    key={v}
                    onClick={() => setForm((p) => ({ ...p, owns_drone: v }))}
                    data-testid={`bw-drone-${v}`}
                    className={`font-mono text-[10px] tracking-[0.18em] uppercase px-8 py-3 rounded-full border transition-all duration-300 ${
                      form.owns_drone === v ? "bg-[#0F2A43] text-white border-[#0F2A43]" : "border-[#E5E7EB] text-[#6B7280] hover:border-[#0F2A43]/40"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <p className="mt-8 font-mono text-[10px] tracking-[0.3em] uppercase text-[#4F6D8A]">Special Requirements</p>
              <textarea
                data-testid="bw-requirements"
                value={form.requirements}
                onChange={set("requirements")}
                rows={4}
                placeholder="Food preference, medical condition, travel assistance..."
                className={`${inputCls} mt-4 resize-none`}
              />
            </motion.div>
          </div>

          {/* Step 3 — sticky summary */}
          <div className="lg:col-span-5 lg:sticky lg:top-28" data-testid="bw-summary">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: 0.1, ease }}
              className="bg-[#0F2A43] rounded-[24px] p-8 shadow-[0_35px_80px_-25px_rgba(15,42,67,0.55)]"
            >
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#8FAECC]">Your Booking</p>
              <div className="mt-6 space-y-4 text-sm">
                {[
                  ["Workshop", "DJI Avata 2 FPV Masterclass"],
                  ["Batch", batchInfo ? `${batchInfo.label} — ${batchInfo.note}` : "October 2026"],
                  ["Duration", "3 Days"],
                  ["Includes", "Stay • Food • Transport"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-4 border-b border-white/10 pb-4">
                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/50">{k}</span>
                    <span className="text-white text-right font-medium">{v}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1">
                  <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/50">Total Payable Today</span>
                  <span className="font-display font-black text-3xl text-white" data-testid="bw-summary-total">
                    ₹{PLANS[plan].amount.toLocaleString("en-IN")}
                  </span>
                </div>
                <p className="font-mono text-[8px] tracking-[0.2em] uppercase text-white/40">
                  {plan === "advance" ? "Advance — baaki workshop se pehle" : "Full payment — instant confirmation"}
                </p>
              </div>
              <p className="mt-6 flex items-center gap-2.5 text-white/60 text-xs">
                <Lock size={13} className="text-[#8FAECC]" /> Secure Checkout — Razorpay
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Step 4 — Payment */}
      <section className="pb-[80px] md:pb-[100px]" data-testid="bw-step-4" ref={payRef}>
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <StepHead n="03" title="Secure Your Spot" />
          {/* scarcity banner */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, ease }}
            className="mb-8 bg-white border border-[#E5E7EB] rounded-full px-7 py-4 flex items-center justify-center gap-3 text-center shadow-[0_10px_30px_-15px_rgba(15,42,67,0.2)]"
            data-testid="bw-scarcity"
          >
            <Zap size={14} className="text-[#4F6D8A] shrink-0" />
            <p className="text-sm text-[#374151]">
              Only <span className="text-[#0F2A43] font-bold">{batchInfo?.remaining ?? 10} seats</span> remain for this batch.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {Object.entries(PLANS).map(([key, p], i) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.55, delay: i * 0.1, ease }}
                data-testid={`bw-plan-${key}`}
                className={`relative bg-white rounded-[22px] border p-8 shadow-[0_12px_35px_-15px_rgba(15,42,67,0.15)] hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-20px_rgba(15,42,67,0.35)] transition-all duration-300 ${
                  key === "advance" ? "border-[#0F2A43] ring-1 ring-[#0F2A43]/20" : "border-[#E5E7EB]"
                }`}
              >
                <span className={`absolute -top-3 left-8 font-mono text-[8px] font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full ${key === "advance" ? "bg-[#0F2A43] text-white" : "bg-[#F8F6F2] border border-[#E5E7EB] text-[#6B7280]"}`}>
                  {p.tag}
                </span>
                <p className="font-display font-bold text-xl text-[#111827]">{p.label}</p>
                <p className="mt-2 text-sm text-[#6B7280]">{p.note}</p>
                <p className="mt-6 font-display font-black text-4xl text-[#0F2A43]">₹{p.amount.toLocaleString("en-IN")}</p>
                <button
                  data-testid={`bw-pay-${key}`}
                  onClick={() => pay(key)}
                  disabled={paying}
                  className={`mt-7 w-full inline-flex items-center justify-center gap-2 font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-6 py-4 rounded-full transition-all duration-300 disabled:opacity-60 ${
                    key === "advance"
                      ? "bg-[#0F2A43] text-white shadow-[0_15px_35px_-12px_rgba(15,42,67,0.5)] hover:bg-[#16395C] hover:-translate-y-0.5"
                      : "bg-white border border-[#0F2A43]/25 text-[#0F2A43] hover:bg-[#EEF2F6] hover:border-[#0F2A43]"
                  }`}
                >
                  {paying ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={13} />} {p.cta}
                </button>
              </motion.div>
            ))}
          </div>

          {/* payment methods */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2.5" data-testid="bw-pay-methods">
            {PAY_METHODS.map((m) => (
              <span key={m} className="font-mono text-[8px] tracking-[0.2em] uppercase text-[#6B7280] bg-white border border-[#E5E7EB] px-4 py-2 rounded-full">
                {m}
              </span>
            ))}
          </div>

          {/* WhatsApp backup */}
          <p className="mt-10 text-center text-sm text-[#6B7280]">
            Prefer to talk first?{" "}
            <a data-testid="bw-whatsapp" href={waHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#0F2A43] font-medium underline underline-offset-4 decoration-[#4F6D8A]/50 hover:decoration-[#0F2A43] transition-all">
              <MessageCircle size={14} /> Chat on WhatsApp
            </a>
          </p>

          {/* Cancellation & refund policy — payment ke saath trust */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.6, ease }}
            className="mt-12 bg-white border border-[#E5E7EB] rounded-[22px] p-8 md:p-9 shadow-[0_15px_40px_-18px_rgba(15,42,67,0.18)]"
            data-testid="bw-refund-policy"
          >
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4F6D8A] flex items-center gap-2.5">
              <ShieldCheck size={13} /> Cancellation & Refund Policy
            </p>
            <div className="mt-6 grid sm:grid-cols-3 gap-5">
              {[
                { t: "14+ Days Before", d: "Full refund — koi sawaal nahi." },
                { t: "7–13 Days Before", d: "50% refund ya next batch mein free transfer." },
                { t: "Under 7 Days", d: "Non-refundable — advance next batch mein ek baar adjust ho sakta hai." },
              ].map((r) => (
                <div key={r.t} className="bg-[#F8F6F2] border border-[#E5E7EB] rounded-2xl px-5 py-5">
                  <p className="font-display font-bold text-sm text-[#0F2A43]">{r.t}</p>
                  <p className="mt-1.5 text-xs text-[#6B7280] leading-relaxed">{r.d}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-xs text-[#6B7280] leading-relaxed">
              Agar hamari taraf se batch cancel ya reschedule ho (weather, permissions), tum choose karte ho: <span className="text-[#0F2A43] font-semibold">100% full refund</span> ya next batch mein priority seat.{" "}
              <a href="/policies/refund-policy" data-testid="bw-refund-link" className="text-[#0F2A43] font-medium underline underline-offset-4 decoration-[#4F6D8A]/50 hover:decoration-[#0F2A43] transition-all">
                Full Refund Policy padho
              </a>
            </p>
          </motion.div>

          {/* trust cards */}
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {TRUST_CARDS.map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.div
                  key={t.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.5, delay: i * 0.06, ease }}
                  className="bg-white rounded-[18px] border border-[#E5E7EB] px-5 py-6 text-center shadow-[0_10px_30px_-15px_rgba(15,42,67,0.15)]"
                >
                  <span className="inline-flex w-10 h-10 rounded-full bg-[#4F6D8A]/10 items-center justify-center">
                    <Icon size={17} strokeWidth={1.4} className="text-[#0F2A43]" />
                  </span>
                  <p className="mt-3 font-display font-bold text-sm text-[#111827]">{t.title}</p>
                  <p className="mt-1 font-mono text-[8px] tracking-[0.18em] uppercase text-[#6B7280]">{t.sub}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mobile sticky bottom CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#E5E7EB] px-5 py-4 flex items-center justify-between gap-4" data-testid="bw-mobile-cta">
        <div>
          <p className="font-mono text-[8px] tracking-[0.2em] uppercase text-[#6B7280]">FPV Masterclass</p>
          <p className="font-display font-bold text-lg text-[#0F2A43]">₹2,000 <span className="text-xs font-medium text-[#6B7280]">advance</span></p>
        </div>
        <button
          data-testid="bw-mobile-reserve"
          onClick={() => payRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className="inline-flex items-center gap-2 bg-[#0F2A43] text-white font-mono text-[10px] font-bold tracking-[0.2em] uppercase px-7 py-3.5 rounded-full shadow-lg"
        >
          Reserve Seat <ArrowRight size={12} />
        </button>
      </div>
      <div className="lg:hidden h-20" />

      <AeroFooter />
      <WhatsAppFloat />
    </motion.main>
  );
}
